import React, { useState, useEffect } from 'react'
import { AlertTriangle, X, Loader2 } from 'lucide-react'

interface ConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
  confirmLabel?: string
  confirmVariant?: 'primary' | 'danger'
  isLoading?: boolean
  children?: React.ReactNode
  /** Require typing this text to enable the confirm button (for destructive actions) */
  requireConfirmText?: string
}

export function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  confirmVariant = 'primary',
  isLoading = false,
  children,
  requireConfirmText,
}: ConfirmationModalProps) {
  const [confirmInput, setConfirmInput] = useState('')
  const [cooldown, setCooldown] = useState(false)

  // Reset state and start cooldown when modal opens
  useEffect(() => {
    if (isOpen) {
      setConfirmInput('')
      if (confirmVariant === 'danger') {
        setCooldown(true)
        const timer = setTimeout(() => setCooldown(false), 2000)
        return () => clearTimeout(timer)
      }
    }
  }, [isOpen, confirmVariant])

  if (!isOpen) return null

  const textMatch = !requireConfirmText || confirmInput.trim().toUpperCase() === requireConfirmText.trim().toUpperCase()
  const isConfirmDisabled = isLoading || cooldown || !textMatch

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-0">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              confirmVariant === 'danger' ? 'bg-red-100' : 'bg-amber-100'
            }`}>
              <AlertTriangle className={`h-5 w-5 ${
                confirmVariant === 'danger' ? 'text-red-600' : 'text-amber-600'
              }`} />
            </div>
            <h3 className="text-lg font-semibold text-secondary-900">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-secondary-100 text-secondary-400 hover:text-secondary-600 transition-colors"
            disabled={isLoading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-secondary-600 text-sm">{message}</p>
          {children && <div className="mt-4">{children}</div>}

          {/* Type-to-confirm input */}
          {requireConfirmText && (
            <div className="mt-4">
              <p className="text-sm text-secondary-600 mb-2">
                Type <span className="font-bold text-red-600">{requireConfirmText}</span> to confirm:
              </p>
              <input
                type="text"
                className="w-full px-3 py-2 text-sm border border-secondary-300 rounded-lg bg-white text-secondary-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                placeholder={requireConfirmText}
                autoFocus
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 pt-0">
          <button
            onClick={onClose}
            className="btn-secondary"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`${confirmVariant === 'danger' ? 'btn-danger' : 'btn-primary'} ${isConfirmDisabled && !isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={isConfirmDisabled}
          >
            {isLoading ? (
              <span className="flex items-center">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </span>
            ) : cooldown ? (
              'Wait...'
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
